import React, { useState } from 'react';
import { useResume } from '../context/ResumeContext';
import { useWorkspaceContext } from '../../../pages/ChatPage/providers/WorkspaceProvider';
import VaultAttachPanel from '../components/VaultAttachPanel';
import moveItem from '../utils/moveItem';
import duplicateItem from '../utils/duplicateItem';
import deleteItem from '../utils/deleteItem';

export default function EducationSection() {
  const { activeResume, updateField } = useResume();
  const { authToken, theme } = useWorkspaceContext();
  const list = activeResume?.education || [];

  const [expandedIndex, setExpandedIndex] = useState(0);

  const handleFieldChange = (idx, field, val) => {
    updateField(`education.${idx}.${field}`, val);
  };

  const handleAddEducation = () => {
    const nextList = [...list, {
      id: `edu_${Date.now()}`,
      college: '',
      degree: '',
      year: '',
      cgpa: ''
    }];
    updateField('education', nextList);
    setExpandedIndex(nextList.length - 1);
  };

  const handleMove = (idx, direction) => {
    const nextList = moveItem(list, idx, direction);
    updateField('education', nextList);
    setExpandedIndex(direction === 'up' ? idx - 1 : idx + 1);
  };

  const handleDuplicate = (idx) => {
    const nextList = duplicateItem(list, idx);
    if (nextList[idx + 1]) {
      nextList[idx + 1].id = `edu_${Date.now()}`;
    }
    updateField('education', nextList);
    setExpandedIndex(idx + 1);
  };

  const handleDelete = (idx) => {
    const nextList = deleteItem(list, idx);
    updateField('education', nextList);
    setExpandedIndex(null);
  };

  const labelStyle = {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: 'var(--text-secondary)',
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    background: 'var(--surface-1)',
    border: '1px solid var(--card-border)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '12.5px',
    outline: 'none',
    marginTop: '4px',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '8px 0' }}>
      {list.map((edu, idx) => {
        const isExpanded = expandedIndex === idx;
        const collegeVal = edu.college || edu.institution;
        const displayName = collegeVal?.trim() || edu.degree?.trim() || `Institution #${idx + 1}`;

        return (
          <div
            key={idx}
            style={{
              background: 'var(--surface-1)',
              border: '1px solid var(--card-border)',
              borderRadius: '10px',
              overflow: 'hidden',
            }}
          >
            {/* Header Accordion Bar */}
            <div
              onClick={() => setExpandedIndex(isExpanded ? null : idx)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                cursor: 'pointer',
                background: isExpanded ? 'rgba(255,255,255,0.02)' : 'transparent',
                borderBottom: isExpanded ? '1px solid var(--divider)' : 'none',
              }}
            >
              <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                {displayName}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} onClick={e => e.stopPropagation()}>
                {/* Order Shifters */}
                <button
                  disabled={idx === 0}
                  onClick={() => handleMove(idx, 'up')}
                  style={{ background: 'transparent', border: 'none', color: idx === 0 ? 'var(--text-muted)' : 'var(--text-secondary)', cursor: 'pointer', fontSize: '11px', opacity: idx === 0 ? 0.4 : 1 }}
                >
                  ▲
                </button>
                <button
                  disabled={idx === list.length - 1}
                  onClick={() => handleMove(idx, 'down')}
                  style={{ background: 'transparent', border: 'none', color: idx === list.length - 1 ? 'var(--text-muted)' : 'var(--text-secondary)', cursor: 'pointer', fontSize: '11px', opacity: idx === list.length - 1 ? 0.4 : 1 }}
                >
                  ▼
                </button>
                
                {/* Duplicate / Delete Buttons */}
                <button
                  onClick={() => handleDuplicate(idx)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '11px', padding: '2px 4px' }}
                  title="Duplicate"
                >
                  📋
                </button>
                <button
                  onClick={() => handleDelete(idx)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--status-danger)', cursor: 'pointer', fontSize: '12px', padding: '2px 4px' }}
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>

            {/* Collapsible Fields Area */}
            {isExpanded && (
              <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>College / University / School</label>
                  <input
                    type="text"
                    value={edu.college || edu.institution || ''}
                    onChange={(e) => handleFieldChange(idx, 'college', e.target.value)}
                    placeholder="e.g. Stanford University"
                    style={inputStyle}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>Degree / Major</label>
                    <input
                      type="text"
                      value={edu.degree || ''}
                      onChange={(e) => handleFieldChange(idx, 'degree', e.target.value)}
                      placeholder="e.g. B.S. in Computer Science"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Graduation Year</label>
                    <input
                      type="text"
                      value={edu.year || ''}
                      onChange={(e) => handleFieldChange(idx, 'year', e.target.value)}
                      placeholder="e.g. 2024"
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Grade / CGPA / Percentage</label>
                  <input
                    type="text"
                    value={edu.cgpa || edu.grade || ''}
                    onChange={(e) => handleFieldChange(idx, 'cgpa', e.target.value)}
                    placeholder="e.g. 3.8/4.0 or 8.5 CGPA"
                    style={inputStyle}
                  />
                </div>

                {/* Vault Attachments */}
                <VaultAttachPanel
                  refs={edu.vault_refs || []}
                  onUpdate={(newRefs) => handleFieldChange(idx, 'vault_refs', newRefs)}
                  authToken={authToken}
                  theme={theme}
                />
              </div>
            )}
          </div>
        );
      })}

      <button
        onClick={handleAddEducation}
        style={{
          width: '100%',
          padding: '12px',
          background: 'transparent',
          border: '1px dashed var(--card-border)',
          borderRadius: '10px',
          color: 'var(--text-primary)',
          fontSize: '13px',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--brand)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--card-border)'}
      >
        <span>+</span> Add Education
      </button>
    </div>
  );
}
